import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { PhoneOff, Phone, Check, ExternalLink } from 'lucide-react';
import { useCentralita, colgar, aceptar, cerrarTrabajo } from '@/hooks/useCentralita';

/** mm:ss desde que se conectó la llamada. */
function Cronometro({ desde }: { desde: number }) {
  const [ahora, setAhora] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.floor((ahora - desde) / 1000));
  return <span className="tabular-nums">{Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}</span>;
}

/**
 * La llamada en curso, visible desde cualquier pantalla del CRM.
 *
 * Existe porque el panel de Amazon Connect solo se ve en la pestaña Centralita,
 * pero la llamada sigue viva al navegar a cualquier otro sitio —esa es toda la
 * gracia de anclar el iframe al layout—. Sin esta barra, quien se fuera a mirar
 * la ficha de un huésped mientras habla se quedaría sin botón de colgar.
 *
 * También resuelve el atasco del trabajo-tras-llamada: al colgar, Connect deja
 * al agente en ACW con su único hueco de voz ocupado, y hasta que alguien lo
 * cierra no se puede volver a llamar. El botón de «Terminar» de aquí es el
 * único a mano cuando el panel no está a la vista.
 */
export default function BarraLlamada() {
  const c = useCentralita();
  const enCentralita = useLocation().pathname.startsWith('/centralita');

  const activa = c.estado === 'marcando' || c.estado === 'sonando' || c.estado === 'hablando';
  const enAcw = c.estado === 'tras-llamada';
  if (!activa && !enAcw) return null;
  // En la propia pestaña ya está el panel entero: la barra sobraría.
  if (enCentralita && !enAcw) return null;

  const rotulo =
    c.estado === 'marcando' ? 'Llamando…'
    : c.estado === 'sonando' ? 'Llamada entrante'
    : c.estado === 'hablando' ? 'En llamada'
    : 'Llamada terminada';

  return (
    <div
      role="status"
      className="fixed z-40 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[340px] card shadow-lg
                 border-sereno-dark/20 dark:border-white/20 py-3 flex items-center gap-3"
    >
      <span
        className={`h-2.5 w-2.5 rounded-full shrink-0 ${
          c.estado === 'hablando' ? 'bg-emerald-500'
          : c.estado === 'sonando' ? 'bg-amber-400 animate-pulse'
          : c.estado === 'marcando' ? 'bg-sky-400 animate-pulse'
          : 'bg-slate-400'
        }`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
          {c.destino?.nombre ?? c.numero ?? rotulo}
        </p>
        <p className="text-xs text-slate-500 truncate">
          {rotulo}
          {c.conectadaEn && <> · <Cronometro desde={c.conectadaEn} /></>}
          {c.destino?.alojamiento && ` · ${c.destino.alojamiento}`}
        </p>
      </div>

      {c.estado === 'sonando' && (
        <button onClick={aceptar} className="btn-primary px-3 py-1.5" aria-label="Descolgar">
          <Phone size={15} />
        </button>
      )}
      {activa && (
        <button
          onClick={colgar}
          className="grid place-items-center h-9 w-9 rounded-md bg-rose-600 text-white hover:bg-rose-700"
          aria-label="Colgar"
        ><PhoneOff size={15} /></button>
      )}
      {enAcw && (
        <button onClick={cerrarTrabajo} className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm">
          <Check size={14} /> Terminar
        </button>
      )}
      {!enCentralita && (
        <Link to="/centralita" className="btn-ghost px-2" aria-label="Abrir la centralita">
          <ExternalLink size={15} />
        </Link>
      )}
    </div>
  );
}
